// Grist API bridge for widget integration - refactored to use RecordProvider

import { STORAGE_KEYS } from '../constants';
import type { MapConfig, GristRecord, ColumnMappings } from '../types';
import type { RecordProvider, RecordProviderEvents } from './record-provider';
import { GristRecordProvider } from './grist-record-provider';

/**
 * Callback types for Grist events
 */
export interface GristCallbacks {
  onRecordsChange?: (records: GristRecord[], config: MapConfig) => void;
  onRecordSelect?: (record: GristRecord | null) => void;
  onConfigLoaded?: (config: MapConfig) => void;
}

/**
 * Default widget configuration
 */
const DEFAULT_CONFIG: MapConfig = {
  nameColumn: '',
  latColumn: '',
  lngColumn: '',
  typeColumn: '',
  typeColorMap: {},
  infoColumns: [],
};

/**
 * Bridge class for Grist API interactions
 * Uses RecordProvider abstraction for data retrieval
 */
export class GristBridge {
  private config: MapConfig = { ...DEFAULT_CONFIG };
  private callbacks: GristCallbacks;
  private currentRecords: GristRecord[] = [];
  private provider: RecordProvider;

  constructor(callbacks: GristCallbacks = {}, provider?: RecordProvider) {
    this.callbacks = callbacks;
    this.provider = provider || new GristRecordProvider();
  }

  /**
   * Initialize Grist widget
   */
  initialize(): void {
    const events: RecordProviderEvents = {
      onRecords: (records, mappings) => {
        this.currentRecords = records;
        this.updateConfigFromMappings(mappings);
        this.callbacks.onRecordsChange?.(records, this.getConfig());
      },
      onRecord: (record) => {
        this.callbacks.onRecordSelect?.(record);
      },
      onOptions: async () => {
        await this.loadConfig();
      },
    };

    this.provider.initialize(events);
    this.loadConfig();
  }

  /**
   * Get current configuration
   */
  getConfig(): MapConfig {
    return { ...this.config };
  }

  /**
   * Get current records
   */
  getRecords(): GristRecord[] {
    return this.currentRecords;
  }

  /**
   * Get the record provider (for testing)
   */
  getProvider(): RecordProvider {
    return this.provider;
  }

  /**
   * Set cursor position in Grist
   * @param recordId - Record ID to select
   */
  async selectRecord(recordId: number): Promise<void> {
    await this.provider.selectRecord(recordId);
  }

  /**
   * Save type color map
   * @param typeColorMap - Color mappings to save
   */
  async saveTypeColors(typeColorMap: Record<string, string>): Promise<void> {
    this.config.typeColorMap = typeColorMap;
    await this.saveConfig();
  }

  /**
   * Update config from column mappings
   */
  private updateConfigFromMappings(mappings: unknown): void {
    if (!mappings) return;

    const mapped = mappings as ColumnMappings;

    if (mapped.Name && typeof mapped.Name === 'string') {
      this.config.nameColumn = mapped.Name;
    }
    if (mapped.Latitude && typeof mapped.Latitude === 'string') {
      this.config.latColumn = mapped.Latitude;
    }
    if (mapped.Longitude && typeof mapped.Longitude === 'string') {
      this.config.lngColumn = mapped.Longitude;
    }
    if (mapped.Type && typeof mapped.Type === 'string') {
      this.config.typeColumn = mapped.Type;
    }
    if (mapped.Info) {
      this.config.infoColumns = Array.isArray(mapped.Info) ? mapped.Info : [mapped.Info];
    } else {
      this.config.infoColumns = [];
    }
  }

  /**
   * Save configuration to Grist and localStorage
   */
  private async saveConfig(): Promise<void> {
    this.config.typeColorMap = this.config.typeColorMap || {};

    try {
      await this.provider.setOption('mapConfig', this.config);
      localStorage.setItem(STORAGE_KEYS.typeColors, JSON.stringify(this.config.typeColorMap));
    } catch (e) {
      console.log('Unable to save configuration:', e);
    }
  }

  /**
   * Load configuration from Grist and localStorage
   */
  private async loadConfig(): Promise<void> {
    try {
      // Load from localStorage first
      const localColors = localStorage.getItem(STORAGE_KEYS.typeColors);
      if (localColors) {
        try {
          this.config.typeColorMap = JSON.parse(localColors);
        } catch (e) {
          console.log('Unable to parse localStorage colors:', e);
        }
      }

      // Then load from Grist (which may override)
      const savedConfig = await this.provider.getOption<MapConfig>('mapConfig');
      if (savedConfig) {
        this.config = {
          ...savedConfig,
          typeColorMap: savedConfig.typeColorMap || this.config.typeColorMap || {},
        };
      }

      this.callbacks.onConfigLoaded?.(this.getConfig());
    } catch (e) {
      console.log('Unable to load configuration:', e);
    }
  }

  /**
   * Destroy bridge and clean up provider
   */
  destroy(): void {
    this.provider.destroy();
  }
}
